import { useSyncExternalStore } from "react";
import { request, RequestError } from "../lib/api";
import type { ModuleDetail, TeacherHome } from "../types";
import { pruneOffline, readCachedHome, readCachedModule, writeCachedHome } from "./assetCache";
import {
  allAssetURLs,
  downloadState,
  ensureModuleDownloaded,
  subscribeDownloads,
} from "./moduleDownload";

// Saves the teacher's PD Day modules (today's and past ones, which stay
// open to revisit) to the phone in the background once they are signed in,
// wherever they are in the app. The header shows its progress
// (SyncIndicator), so there is no separate download screen.

export type SyncStatus = "idle" | "syncing" | "done" | "error";

let moduleIDs: string[] = [];
let failed = false;
let finished = false;
let running: Promise<void> | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

async function prune(keepModuleIDs: string[]): Promise<void> {
  const modules = (await Promise.all(keepModuleIDs.map((id) => readCachedModule(id)))).filter(
    (module): module is ModuleDetail => Boolean(module)
  );
  await pruneOffline(
    keepModuleIDs,
    modules.flatMap((module) => module.items.flatMap(allAssetURLs))
  );
}

async function sync(): Promise<void> {
  failed = false;
  notify();
  let home: TeacherHome | null;
  let fromNetwork = false;
  try {
    home = await request<TeacherHome>("/api/teacher/home");
    fromNetwork = true;
    void writeCachedHome(home);
  } catch (cause) {
    home = cause instanceof RequestError ? null : await readCachedHome();
  }
  if (!home) {
    failed = true;
    return;
  }
  // Today's PD Day first, then past ones one at a time, so today's
  // lessons are ready soonest on a weak connection.
  const { modules } = home;
  const byState = (state: string) =>
    modules.filter((module) => module.state === state).map((module) => module.id);
  const today = byState("current");
  const past = byState("past");
  moduleIDs = [...today, ...past];
  notify();
  await Promise.all(today.map((id) => ensureModuleDownloaded(id)));
  for (const id of past) await ensureModuleDownloaded(id);
  // Only tidy up against a list the server just confirmed; a cached list
  // may be stale and would delete a module still in use.
  if (fromNetwork) await prune(moduleIDs);
}

// Starts a sync, or joins the one already running. Safe to call often:
// files already on the phone are only checked, not downloaded again.
export function startBackgroundSync(): Promise<void> {
  if (!running) {
    running = sync().finally(() => {
      running = null;
      finished = true;
      notify();
    });
    notify();
  }
  return running;
}

// Forgets the previous teacher's modules on sign-out.
export function resetBackgroundSync(): void {
  moduleIDs = [];
  failed = false;
  finished = false;
  notify();
}

function status(): SyncStatus {
  const phases = moduleIDs.map((id) => downloadState(id).phase);
  if (running || phases.some((phase) => phase === "loading" || phase === "downloading")) {
    return "syncing";
  }
  if (failed || phases.includes("error")) return "error";
  return finished ? "done" : "idle";
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const unsubscribeDownloads = subscribeDownloads(listener);
  return () => {
    listeners.delete(listener);
    unsubscribeDownloads();
  };
}

export function useBackgroundSync(): SyncStatus {
  return useSyncExternalStore(subscribe, status);
}
